import { useState, type JSX, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApp, type Screen } from '../state/app.js';
import { expenseApi } from '../api/expense.js';
import { isEmbedded } from '../auth/authService.js';
import { APP_VERSION, BUILD_TIME_LABEL } from '../buildInfo.js';
import { Avatar } from '../ui/common.js';
import { MyProfileModal } from '../modals/MyProfileModal.js';
import {
  IconAdmin, IconApprove, IconBoard, IconCI, IconCompany, IconHome, IconLeave, IconLibrary, IconPicker, IconResources, IconSettings, IconStats, IconPeopleStats, IconTrip, IconWeekly, IconExpense, IconOvertime,
} from '../ui/icons.js';

function NavItem({ active, onClick, icon, label, badge }: {
  active: boolean; onClick: () => void; icon: ReactNode; label: string; badge?: number;
}): JSX.Element {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 11, width: '100%', border: 'none', textAlign: 'left',
      fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600, padding: '9px 11px', borderRadius: 8, cursor: 'pointer',
      background: active ? '#EFF4FE' : 'transparent', color: active ? '#1D4ED8' : '#4B5563',
    }}>
      {icon}{label}
      {badge ? (
        <span style={{
          marginLeft: 'auto', background: '#EF4444', color: '#fff', fontSize: 11, fontWeight: 700,
          minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>{badge}</span>
      ) : null}
    </button>
  );
}

export function Sidebar({ pendingCount = 0 }: { pendingCount?: number }): JSX.Element {
  const { me, screen, setScreen, roleLabel } = useApp();
  const p = me.permissions;
  const go = (s: Screen) => () => setScreen(s);
  const [profileOpen, setProfileOpen] = useState(false);
  // CEO: 결재·지급할 경비 건수 배지
  const isCeo = me.roles.includes('CEO');
  const expenseAll = useQuery({ queryKey: ['expenses', 'all'], queryFn: () => expenseApi.list('all'), enabled: isCeo, refetchInterval: 120_000 });
  const expenseBadge = isCeo ? (expenseAll.data?.items ?? []).filter((e) => e.status === 'submitted' || e.status === 'approved').length : 0;

  // Teams/Outlook 임베드 시 호스트가 이미 앱 이름을 표시하므로 사이드바 로고는 중복 → 숨김
  const embedded = isEmbedded();

  return (
    <div style={{ width: 236, flexShrink: 0, background: '#fff', borderRight: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', padding: '16px 12px', height: '100%' }}>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px 18px' }}>
          <img src="/brand/DXTECH_MARK.svg" alt="DXTECH" style={{ width: 28, height: 28 }} />
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-.01em' }}>DXTECH Works</div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: embedded ? 4 : 0 }}>
        <NavItem active={screen === 'home'} onClick={go('home')} icon={<IconHome />} label="홈" />
        {/* 근무현황 — 전 임직원(상태·일정), 출퇴근 시각은 CEO·임원·관리자만 */}
        <NavItem active={screen === 'admin'} onClick={go('admin')} icon={<IconAdmin />} label="근무현황" />
        <NavItem active={screen === 'overtime'} onClick={go('overtime')} icon={<IconOvertime />} label="야근" />
        <NavItem active={screen === 'leave'} onClick={go('leave')} icon={<IconLeave />} label="휴가" />
        <NavItem active={screen === 'trip'} onClick={go('trip')} icon={<IconTrip />} label="출장" />
        <NavItem active={screen === 'weekly'} onClick={go('weekly')} icon={<IconWeekly />} label="주간보고" />
        <NavItem active={screen === 'expense'} onClick={go('expense')} icon={<IconExpense />} label="경비지출" badge={expenseBadge} />
        <NavItem active={screen === 'company'} onClick={go('company')} icon={<IconCompany />} label="회사 정보" />
        <NavItem active={screen === 'ci'} onClick={go('ci')} icon={<IconCI />} label="CI" />
        <NavItem active={screen === 'resources'} onClick={go('resources')} icon={<IconResources />} label="문서함" />
        <NavItem active={screen === 'library'} onClick={go('library')} icon={<IconLibrary />} label="자료실" />
        <NavItem active={screen === 'picker'} onClick={go('picker')} icon={<IconPicker />} label="랜덤피커" />
        {p.canApproveLeave && <NavItem active={screen === 'approve'} onClick={go('approve')} icon={<IconApprove />} label="휴가 승인" badge={pendingCount} />}
        <NavItem active={screen === 'board'} onClick={go('board')} icon={<IconBoard />} label="임직원 보드" />
        {p.canViewAdmin && <NavItem active={screen === 'stats'} onClick={go('stats')} icon={<IconStats />} label="근무통계분석" />}
        <NavItem active={screen === 'peopleStats'} onClick={go('peopleStats')} icon={<IconPeopleStats />} label="임직원통계분석" />
        {p.canManageConfig && <NavItem active={screen === 'settings'} onClick={go('settings')} icon={<IconSettings />} label="관리자 설정" />}
      </div>
      {/* 내 정보 — 이름·직책 영역 전체가 버튼(생일·주소·연락처 수정) */}
      <div style={{ marginTop: 'auto', borderTop: '1px solid #F3F4F6', paddingTop: 8 }}>
        <button className="dx-me-btn" onClick={() => setProfileOpen(true)} title="내 정보 수정 (생일·주소·연락처)">
          <Avatar name={me.name} upn={me.upn} size={34} />
          <span style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{me.name}</span>
            <span style={{ display: 'block', fontSize: 11, color: '#9CA3AF' }}>{roleLabel}{me.dept ? ` · ${me.dept}` : ''}</span>
          </span>
          <span className="dx-me-btn-tag">내 정보 ›</span>
        </button>
      </div>
      {profileOpen && <MyProfileModal onClose={() => setProfileOpen(false)} />}
      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #F3F4F6', textAlign: 'center', lineHeight: 1.5 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748B' }}>DXTECH Works v{APP_VERSION}</div>
        <div style={{ fontSize: 10.5, color: '#9CA3AF' }}>Build {BUILD_TIME_LABEL}</div>
      </div>
    </div>
  );
}
